/**
 * PaymentMethodCard — manage payment method via Stripe Customer Portal
 * (design Screen 4, middle section).
 *
 * v1 does not render the saved card brand / last4 in-app (that lives in the
 * Stripe-hosted Customer Portal). This card is a single affordance: "Manage
 * payment method" → opens the Portal via `useCustomerPortal().openPortal()`.
 * While the redirect is in flight the button shows a loading label and is
 * disabled (we never strand the user mid-redirect — see the hook).
 *
 * Hidden for free Explorer users, who have no payment method on file (design
 * Screen 4 — "Empty plan / No payment method": section hidden).
 *
 * @param {object} props
 * @param {object|null} props.subscription - GET /api/billing/subscription shape;
 *   used only to decide whether to render (paid plans only).
 * @param {() => void} props.onManage - Opens the Stripe Customer Portal.
 * @param {boolean} [props.loading] - Whether the portal redirect is in flight.
 * @param {string|null} [props.error] - Portal open error to surface inline.
 */

import React from 'react';
import styles from './PaymentMethodCard.module.css';

/** Paid plans that have a payment method on file. */
const PAID_PLANS = new Set(['fullservice']);

export default function PaymentMethodCard({
  subscription,
  onManage,
  loading,
  error,
}) {
  const planCode = subscription?.plan_code || 'explorer';
  const status = subscription?.status || null;

  // Explorer / no-status users have no card on file — hide the section entirely.
  if (!PAID_PLANS.has(planCode) || !status) {
    return null;
  }

  return (
    <div className={styles.card} data-testid="payment-method-card">
      <p className={styles.copy}>
        Your card is managed securely by Stripe. Update it, view receipts, or
        change your plan in the Stripe Customer Portal.
      </p>

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}

      <button
        type="button"
        className={styles.button}
        onClick={onManage}
        disabled={loading}
        aria-busy={loading}
        data-testid="manage-payment-btn"
      >
        {loading ? 'Opening Stripe…' : 'Manage payment method'}
      </button>
    </div>
  );
}
